import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Injector,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  computed,
  forwardRef,
  input,
  signal,
  viewChild
} from '@angular/core';
import { InputColor, InputSize } from '../inputs.models';
import { filter, fromEvent, map, of, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CHECKBOX_HOST } from '../inputs.constants';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl
} from '@angular/forms';

@Component({
  selector: 'checkbox',
  standalone: true,
  host: CHECKBOX_HOST,
  template: `<label
    [attr.for]="id() ? id() : null"
    class="input-checkbox"
    [class]="_classList()"
    tabindex="0"
    (keyup)="handleKeyupEvent($event)"
    (blur)="onTouched()"
  >
    <input
      #nativeCheckbox
      type="checkbox"
      class="input-checkbox__native-input"
      [attr.id]="id() ? id() : null"
      [attr.checked]="isChecked() ? 'true' : null"
      [attr.disabled]="isDisabled() ? 'true' : null"
      [attr.readonly]="readonly() ? 'true' : null"
      [attr.name]="inputName() ?? null"
      [value]="value()"
      tabindex="-1"
    />
    <span class="input-checkbox__indicator-wrapper">
      <span class="input-checkbox__indicator">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="10"
          viewBox="0 0 12.66 9.458"
        >
          <path
            id="Tracé_661"
            data-name="Tracé 661"
            d="M-16348.611,2481l2.788,2.788,7.044-7.044"
            transform="translate(16350.025 -2475.33)"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
          />
        </svg>
      </span>
      <span class="input-checkbox__indicator-background"></span>
    </span>
    <span class="input-checkbox__label"><ng-content></ng-content></span>
  </label>`,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CheckboxComponent)
    }
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckboxComponent
  implements AfterViewInit, OnChanges, ControlValueAccessor
{
  @Output() selectionChange = new EventEmitter<string | boolean | null>();
  uniqueId = input.required<string>();
  value = input<string | undefined | null>(null);
  checked = input(false);
  disabled = input(false);
  readonly = input(false);
  color = input<InputColor>('secondary');
  size = input<InputSize>('md');
  labelledby = input<string | undefined | null>(null);
  inputName = input<string | undefined | null>(null);

  nativeCheckboxChild =
    viewChild<ElementRef<HTMLInputElement>>('nativeCheckbox');

  id = signal('');
  isChecked = signal(false);
  isDisabled = signal(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private get _control(): AbstractControl<any, any> | null | undefined {
    const ngControl = this._injector.get(NgControl, undefined, {
      optional: true,
      self: true
    });
    return ngControl?.control;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void = () => {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onTouched: () => any = () => {};

  protected _classList = computed(() => {
    return `input-checkbox--${this.size()} input-checkbox--${this.color()} ${this.isChecked() ? 'input-checkbox--checked' : ''} ${this.isDisabled() ? 'input-checkbox--disabled' : ''} ${this.readonly() ? 'input-checkbox--readonly' : ''}`;
  });

  protected _labelledby = computed(() => this.labelledby());

  private get _nativeCheckbox(): HTMLInputElement | undefined {
    return this.nativeCheckboxChild()?.nativeElement;
  }

  get destroyRef(): DestroyRef {
    return this._destroyRef;
  }

  constructor(
    private _destroyRef: DestroyRef,
    private _injector: Injector
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('checked' in changes) {
      this.isChecked.set(this.checked());
    }

    if ('disabled' in changes) {
      this.isDisabled.set(this.disabled());
    }

    if ('uniqueId' in changes) {
      if (!this.uniqueId().length || this.uniqueId().includes(' ')) {
        throw new Error('uniqueId must not be empty or contain spaces.');
      } else {
        this.id.set(this.uniqueId());
      }
    }
  }

  ngAfterViewInit(): void {
    this._handleNativeInputCheckedStatusChanges();
  }

  writeValue(value: unknown): void {
    if (typeof value === 'boolean') {
      this.isChecked.set(value);
      return;
    }

    if (typeof value === 'string') {
      this.isChecked.set(value === this.value());
      return;
    }

    this.isChecked.set(false);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  private _handleNativeInputCheckedStatusChanges(): void {
    if (!(this._nativeCheckbox && this.id() && !this.readonly())) return;

    fromEvent(this._nativeCheckbox, 'change')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(e => e.target as HTMLInputElement | null),
        filter(Boolean),
        switchMap(element => {
          this.isChecked.set(element.checked);
          if (!this.value()) {
            this.onChange(element.checked);
            this.selectionChange.emit(element.checked);
          } else {
            this.onChange(this.isChecked() ? this.value() : null);
            this.selectionChange.emit(this.isChecked() ? this.value() : null);
          }
          return of(this.isChecked());
        })
      )
      .subscribe();
  }

  handleKeyupEvent(event: KeyboardEvent): void {
    if (event.code === 'Space' || event.code === 'Enter') {
      this._nativeCheckbox?.click();
    }
  }
}
